import { environmentMismatch, supabase, authUrlParams } from '@/lib/supabase';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TEST_IDS } from '@/constants/testIds/alvora';
import { AuthShell } from '@/components/auth/AuthShell';

export default function LoginPage() {
  const { signIn, sendPasswordReset, supabaseConfigured } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(new URLSearchParams(location.search).get('mode') === 'reset' ? 'reset' : 'signin'); // 'signin' | 'reset'
  const [resetSent, setResetSent] = useState(false);
  // Recovery links that land here (Auth Site URL fallback) are forwarded to the reset page, including expired-link errors
  useEffect(() => { if (new URLSearchParams(window.location.search).get('mode') === 'reset') return; if (authUrlParams.get('type') === 'recovery' || authUrlParams.get('error_code')) navigate('/reset-password' + window.location.hash, { replace: true }); }, [navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { user: u } = await signIn(email.trim(), password);
      // CP-1: portal-only users (no organisation membership) land in /portal
      const { data: mem } = await supabase.from('organisation_members').select('organisation_id').eq('user_id', u.id).limit(1);
      navigate(mem?.length ? from : '/portal', { replace: true });
    } catch (err) {
      setError(err?.message || 'Unable to sign in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setResetSent(false);
    setSubmitting(true);
    try {
      await sendPasswordReset(email.trim());
      setResetSent(true);
    } catch (err) {
      setError(err?.message || 'Unable to send reset email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell testId="login-page">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight text-charcoal">
            {mode === 'signin' ? 'Sign in' : 'Reset password'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === 'signin'
              ? 'Welcome back. Enter your work email to continue.'
              : 'Enter your email and we will send a reset link.'}
          </p>

          {!supabaseConfigured && (
            <div className="mt-5 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800 flex gap-2">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span data-testid="env-error">{environmentMismatch ? 'Blocked: this production site is configured against a non-production database. Contact the administrator.' : 'Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'}</span>
            </div>
          )}

          <form
            onSubmit={mode === 'signin' ? handleSignIn : handleReset}
            className="mt-6 space-y-4"
          >
            <div>
              <Label htmlFor="email" className="text-xs font-medium text-charcoal">
                Email
              </Label>
              <div className="mt-1 relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@alvoraconstruction.ie"
                  data-testid={mode === 'signin' ? TEST_IDS.loginEmail : TEST_IDS.resetEmail}
                  className="h-11 pl-9"
                />
              </div>
            </div>

            {mode === 'signin' && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-medium text-charcoal">
                    Password
                  </Label>
                  <button
                    type="button"
                    data-testid={TEST_IDS.loginForgot}
                    onClick={() => { setMode('reset'); setError(''); setResetSent(false); }}
                    className="text-xs text-bronze hover:text-bronze-hover transition-colors duration-150"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="mt-1 relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid={TEST_IDS.loginPassword}
                    className="h-11 pl-9"
                  />
                </div>
              </div>
            )}

            {error && (
              <div
                data-testid={TEST_IDS.loginError}
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start gap-2"
              >
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {resetSent && (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                <span>If an account exists for {email}, a reset link has been sent.</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !supabaseConfigured}
              data-testid={mode === 'signin' ? TEST_IDS.loginSubmit : TEST_IDS.resetSubmit}
              className="w-full h-11 bg-bronze hover:bg-bronze-hover text-white font-medium transition-colors duration-150"
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  {mode === 'signin' ? 'Signing in…' : 'Sending link…'}
                </span>
              ) : mode === 'signin' ? (
                'Sign in'
              ) : (
                'Send reset link'
              )}
            </Button>

            {mode === 'reset' && (
              <button
                type="button"
                onClick={() => { setMode('signin'); setError(''); setResetSent(false); }}
                className="w-full text-center text-xs text-muted-foreground hover:text-charcoal transition-colors duration-150"
              >
                Back to sign in
              </button>
            )}
          </form>

          <div className="mt-10 text-[11px] tracking-wider text-muted-foreground text-center lg:hidden">
            © {new Date().getFullYear()} Alvora Construction Ltd
          </div>
        </div>
    </AuthShell>
  );
}
